import { useEffect, useRef, useState } from "react";
import { Loader2, Send, RotateCcw, Sparkles, Printer, Play, Volume2, VolumeX, Shuffle } from "lucide-react";
import { SCENES } from "@/lib/scenes";
import { toast } from "sonner";
import { MicButton } from "@/components/MicButton";
import { AcornCelebration } from "@/components/AcornCelebration";
import { practiceTurn, completePractice, speak, stopSpeaking } from "@/lib/api";

let seq = 0;
const newId = () => ++seq;

const Bubble = ({ line, name }) => (
  <div className={`pp-bubble ${line.role}`} data-testid={`practice-line-${line.role}`}>
    <span className="pp-bubble-who">{line.role === "character" ? name : "You"}
      {line.role === "character" && (
        <button className="pp-replay" onClick={() => speak(line.text)} aria-label="Hear it again" data-testid="practice-replay-button"><Volume2 className="h-3 w-3" /></button>
      )}
    </span>
    <p>{line.text}</p>
    {line.spotted?.length > 0 && (
      <div className="flex flex-wrap gap-1 mt-2">
        {line.spotted.map((s) => <span key={s} className="pp-spotted"><Sparkles className="h-3 w-3" /> {s}</span>)}
      </div>
    )}
  </div>
);

export const PracticeChat = ({ scenario, age, profile, onEarned, variant }) => {
  const [scene, setScene] = useState(null);
  const name = scene?.who || variant?.character || scenario.character;
  const firstOf = (sc) => ({ id: newId(), role: "character", text: (sc || variant || scenario).opening[age] });
  const first = firstOf(scene);
  const scenes = SCENES[scenario.id] || [];
  const [lines, setLines] = useState([first]);
  const [coach, setCoach] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [reward, setReward] = useState(null);
  const [started, setStarted] = useState(false);
  const [voiceOn, setVoiceOn] = useState(localStorage.getItem("pp_practice_voice") !== "off");
  const end = useRef(null);
  const say = (t) => voiceOn && speak(t);
  const toggleVoice = () => { if (voiceOn) stopSpeaking(); localStorage.setItem("pp_practice_voice", voiceOn ? "off" : "on"); setVoiceOn(!voiceOn); };
  const start = () => { setStarted(true); say(first.text); };
  const newScene = () => {
    const pool = [null, ...scenes].filter((x) => x !== scene);
    const next = pool[Math.floor(Math.random() * pool.length)];
    setScene(next); setLines([firstOf(next)]); setCoach(""); setReward(null); setText("");
    if (started) say(firstOf(next).text);
  };

  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [lines, coach]);

  const finish = async (all) => {
    try {
      const saved = await completePractice({
        age, scenario: scenario.id, transcript: all.map(({ role, text: t }) => ({ role, text: t })),
        profile_id: profile?.id, profile_name: profile?.name,
      });
      setReward(saved);
      onEarned();
    } catch {
      toast.error("We couldn't save your practice. Please try again.");
    }
  };

  const send = async (value = text) => {
    if (!value.trim() || busy) return;
    const child = { id: newId(), role: "child", text: value.trim() };
    let all = [...lines, child];
    setLines(all);
    setText("");
    setBusy(true);
    try {
      const r = await practiceTurn({ scenario: scenario.id, age, variant: variant?.id, scene_character: scene?.character, history: all.map(({ role, text: t }) => ({ role, text: t })) });
      all = all.map((l, i) => (i === all.length - 1 ? { ...l, spotted: r.spotted } : l));
      if (r.reply) all = [...all, { id: newId(), role: "character", text: r.reply }];
      setLines(all);
      setCoach(r.coach);
      if (r.reply) say(r.reply);
      if (r.done) await finish(all);
    } catch { toast.error("Your practice buddy is busy. Try again."); }
    setBusy(false);
  };

  const restart = () => { setLines([first]); setCoach(""); setReward(null); say(first.text); };

  return (
    <section className={`pp-card ${reward ? "gate-ok" : ""}`} data-testid="practice-chat">
      {reward && <AcornCelebration age={age} answer="" golden={reward.golden ? 1 : 0} />}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="pp-label">Practice · with {name}</p>
        <div className="flex gap-2 flex-wrap">
        {scenes.length > 0 && (
          <button className="pp-ghost pp-voice-toggle" onClick={newScene} disabled={busy} data-testid="practice-new-scene-button">
            <Shuffle className="h-4 w-4" /> New scene
          </button>
        )}
        <button className="pp-ghost pp-voice-toggle" onClick={toggleVoice} aria-pressed={voiceOn} data-testid="practice-voice-toggle">
          {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />} Voice {voiceOn ? "on" : "off"}
        </button>
        </div>
      </div>
      {!started ? (
        <div className="pp-ready" data-testid="practice-ready">
          <p className="pp-h2">Ready to practice with {name}?</p>
          <p className="opacity-75">Read the key manners first. Tap start when you're ready{voiceOn ? ` and ${name} will start talking` : ""}.</p>
          <button className="pp-primary" onClick={start} data-testid="practice-start-button"><Play className="h-5 w-5" /> I'm ready, start!</button>
        </div>
      ) : (<>
      <div className="pp-chat" data-testid="practice-transcript">
        {lines.map((l) => <Bubble key={l.id} line={l} name={name} />)}
        {busy && <p className="pp-sub flex gap-2" data-testid="practice-thinking"><Loader2 className="h-4 w-4 animate-spin" /> …</p>}
        <div ref={end} />
      </div>
      {coach && <p className="pp-feedback small" data-testid="practice-coach-note">{coach}</p>}
      {reward ? (
        <>
          <p className={`pp-reward ${reward.golden ? "gold" : ""}`} data-testid="practice-reward">
            <img src="/acorn-t.png" alt="" /> +{reward.acorns} acorn{reward.acorns > 1 ? "s" : ""}
            {reward.golden && <b> · golden for gratitude words!</b>}
          </p>
          <div className="pp-actions">
            {scenario.printable && (
              <button className="pp-ghost" data-testid="print-note-button" onClick={() => {
                localStorage.setItem("pp_last_note", [...lines].reverse().find((l) => l.role === "child")?.text || "");
                window.open("/print/note", "_blank");
              }}><Printer className="h-4 w-4" /> Print my note</button>
            )}
            <button className="pp-primary" onClick={restart} data-testid="practice-restart-button"><RotateCcw className="h-5 w-5" /> Practice again</button>
          </div>
        </>
      ) : (
        <div className="pp-practice-input">
          {scenario.multiline ? (
            <textarea className="pp-textarea pp-email-input" rows={8} value={text} disabled={busy} data-testid="practice-input"
              placeholder={scenario.placeholder || `Subject: \n\nDear ${name},\n\n…\n\nThank you,\nYour name`} onChange={(e) => setText(e.target.value)} />
          ) : (
            <input className="pp-input" value={text} placeholder="Your reply…" disabled={busy} data-testid="practice-input"
              onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
          )}
          <MicButton testId="practice-mic-button" onText={send} label="Say it" />
          <button className="pp-primary" onClick={() => send()} disabled={busy || !text.trim()} data-testid="practice-send-button">
            <Send className="h-5 w-5" />
          </button>
        </div>
      )}
      </>)}
    </section>
  );
};
